import { useEffect, useMemo, useRef, useState } from 'react';
import { EM_DASH, HFBox, HFButton, HFCard, HFDataTable, HFSkeleton, HFTooltip, InfoIcon, type HFGridColDef } from '@hf/designSystem/v1';
import {
  OMS_LAYOUT_COLUMN_MAX_WIDTH,
  OMS_LAYOUT_COLUMN_MIN_WIDTH,
  OMS_POSITION_COLUMN_IDS,
  type OmsLayoutColumn,
  type OmsPosition,
  type OmsPositionColumnId,
  type OmsPositionFilters,
  type OmsPositionView,
} from '@hf/sharedContract';
import { GA, track } from '@core/utils/analytics';
import { csvFileName, downloadCsv, toCsv } from '@core/utils/csv';
import { useHttp } from '@core/utils/http';
import { monitorPaneSx } from '../layout/monitorPane';
import { loadMonitorLayout, monitorLayoutSaveActive, queueMonitorLayoutSave, syncMonitorLayout } from '../layout/monitorLayoutWrite';
import { PanelHeader } from './PanelHeader';
import { TableSkeleton } from './TableSkeleton';

type PositionRow = {
  id: string;
  symbol: string;
  bid: string;
  last: string;
  ask: string;
  long: number | null;
  short: number | null;
  opening: number | null;
  filled: number;
  working: number;
  closing: number | null;
  closePlusOrders: number | null;
  daily: string;
  mtd: string;
  qtd: string;
  ytd: string;
};

const VIEWS: Array<{ id: OmsPositionView; label: string }> = [
  { id: 'total', label: 'Contract' },
  { id: 'tier', label: 'Fund, then contract' },
  { id: 'account', label: 'Account, then contract' },
];

const COLUMN_HINTS: Partial<Record<keyof PositionRow, string>> = {
  opening: 'Opening positions are not connected yet, so Opening, Closing and Close+Orders are blank.',
  filled: 'Fills are on the Trade Blotter tab.',
  working: 'Working is every submitted order that has not filled yet (pending included).',
  closing: 'Closing = Opening + Filled.',
  closePlusOrders: 'Close + Orders = Opening + Filled + Working.',
  daily: 'P&L columns fill in step 7.',
  mtd: 'P&L columns fill in step 7.',
  qtd: 'P&L columns fill in step 7.',
  ytd: 'P&L columns fill in step 7.',
};
const GROUP_WIP = 'WIP - under construction';
const NO_MARKET_VALUE = 'Market value is not shown on this grid.';
const COMPACT_BUTTON_SX = { minHeight: 28, px: 1.25, py: 0.25, fontSize: 12 } as const;

const HEADER_SX = {
  fontWeight: 800,
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

function ColumnHint({ label, hint }: { label: string; hint?: string }) {
  return (
    <HFBox component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ...HEADER_SX }}>
      {label}
      {hint ? (
        <HFTooltip title={hint}>
          <HFBox
            component="button"
            type="button"
            aria-label={hint}
            sx={{ display: 'inline-flex', p: 0, border: 0, bgcolor: 'transparent', color: 'text.secondary', cursor: 'help', lineHeight: 0 }}
          >
            <InfoIcon sx={{ fontSize: 14 }} />
          </HFBox>
        </HFTooltip>
      ) : null}
    </HFBox>
  );
}

function signed(value: number | null): string {
  return value == null ? EM_DASH : String(value);
}

function toRow(position: OmsPosition): PositionRow {
  const opening = position.opening;
  return {
    id: position.positionKey,
    symbol: position.bbFullSymbol,
    bid: EM_DASH,
    last: position.latestClose == null ? EM_DASH : String(position.latestClose),
    ask: EM_DASH,
    long: opening != null && opening > 0 ? opening : null,
    short: opening != null && opening < 0 ? opening : null,
    opening,
    filled: position.filled,
    working: position.working,
    closing: position.closing,
    closePlusOrders: position.closePlusOrders,
    daily: EM_DASH,
    mtd: EM_DASH,
    qtd: EM_DASH,
    ytd: EM_DASH,
  };
}

function Qty({ value, tone }: { value: number | null; tone: 'long' | 'short' | 'signed' }) {
  const text = signed(value);
  const negative = value != null && value < 0;
  const color = text === EM_DASH ? 'text.secondary' : tone === 'long' ? 'success.main' : tone === 'short' || negative ? 'error.main' : 'text.primary';
  return (
    <HFBox component="span" sx={{ color }}>
      {text}
    </HFBox>
  );
}

function EmptyPositions({ message }: { message: string }) {
  return <HFBox sx={{ px: 2, py: 3, color: 'text.secondary', fontSize: 14 }}>{message}</HFBox>;
}

const POSITION_COLUMN_DETAILS: Record<OmsPositionColumnId, { label: string; width: number; tone?: 'long' | 'short' | 'signed' }> = {
  symbol: { label: 'BB full symbol', width: 170 },
  bid: { label: 'Bid', width: 80 },
  last: { label: 'Last', width: 90 },
  ask: { label: 'Ask', width: 80 },
  long: { label: 'Long', width: 80, tone: 'long' },
  short: { label: 'Short', width: 80, tone: 'short' },
  opening: { label: 'Opening', width: 120, tone: 'signed' },
  filled: { label: 'Filled', width: 110, tone: 'signed' },
  working: { label: 'Working', width: 120, tone: 'signed' },
  closing: { label: 'Closing', width: 120, tone: 'signed' },
  closePlusOrders: { label: 'Close+ord', width: 140, tone: 'signed' },
  daily: { label: 'Daily', width: 100 },
  mtd: { label: 'MTD', width: 90 },
  qtd: { label: 'QTD', width: 90 },
  ytd: { label: 'YTD', width: 90 },
};

function defaultPositionColumns(): OmsLayoutColumn[] {
  return OMS_POSITION_COLUMN_IDS.map((id) => ({ id, width: POSITION_COLUMN_DETAILS[id].width, visible: true }));
}

function sameColumns(left: OmsLayoutColumn[], right: OmsLayoutColumn[]): boolean {
  return (
    left.length === right.length &&
    left.every((column, index) => column.id === right[index]?.id && column.width === right[index]?.width && column.visible === right[index]?.visible)
  );
}

function applyFieldOrder(columns: OmsLayoutColumn[], fields: string[]): OmsLayoutColumn[] {
  const byId = new Map(columns.map((column) => [column.id, column]));
  const next = fields.map((id) => byId.get(id)).filter((column): column is OmsLayoutColumn => column != null);
  const seen = new Set(next.map((column) => column.id));
  for (const column of columns) {
    if (!seen.has(column.id)) next.push(column);
  }
  return next;
}

function applySavedPositionColumns(saved: OmsLayoutColumn[]): OmsLayoutColumn[] {
  const defaults = defaultPositionColumns();
  const known = new Map(defaults.map((column) => [column.id, column]));
  const used = new Set<string>();
  const next: OmsLayoutColumn[] = [];
  for (const column of saved) {
    const base = known.get(column.id);
    if (!base || used.has(column.id)) continue;
    used.add(column.id);
    next.push({ id: column.id, width: column.width ?? base.width, visible: true });
  }
  for (const column of defaults) {
    if (!used.has(column.id)) next.push(column);
  }
  return next;
}

function column(field: keyof PositionRow, headerName: string, width: number, tone?: 'long' | 'short' | 'signed'): HFGridColDef<PositionRow> {
  return {
    field,
    headerName,
    width,
    minWidth: OMS_LAYOUT_COLUMN_MIN_WIDTH,
    maxWidth: OMS_LAYOUT_COLUMN_MAX_WIDTH,
    sortable: false,
    renderHeader: () => <ColumnHint label={headerName} hint={COLUMN_HINTS[field]} />,
    ...(tone ? { renderCell: ({ row }: { row: PositionRow }) => <Qty value={row[field] as number | null} tone={tone} /> } : null),
  };
}

/** Read-only positions under the Order Monitor blotter. The read is shared with the blotter's instrument rows, so both show the same figures. */
export function PositionsGrid({
  date,
  positions,
  failed,
  compact = false,
  initialFilters,
  onFiltersChange,
  initialColumns,
  onColumnsChange,
}: {
  date?: string;
  positions: readonly OmsPosition[] | null;
  failed: boolean;
  /** Inside the Custom layout: a one-line header, with the toolbar behind a Filters toggle. */
  compact?: boolean;
  /** Read once, when the grid mounts. */
  initialFilters?: OmsPositionFilters;
  onFiltersChange?: (filters: OmsPositionFilters) => void;
  /** Compact only, read once. The Custom layout keeps the columns and never touches the personal layout. */
  initialColumns?: OmsLayoutColumn[];
  onColumnsChange?: (columns: OmsLayoutColumn[]) => void;
}) {
  const { request } = useHttp();
  const [view, setView] = useState<OmsPositionView>(initialFilters?.groupBy ?? 'total');
  const [chromeOpen, setChromeOpen] = useState(false);
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  useEffect(() => onFiltersChangeRef.current?.({ groupBy: view }), [view]);
  const [layoutColumns, setLayoutColumns] = useState<OmsLayoutColumn[]>(() =>
    compact && initialColumns ? applySavedPositionColumns(initialColumns) : defaultPositionColumns(),
  );
  const [layoutReady, setLayoutReady] = useState(compact);
  const layoutColumnsRef = useRef(layoutColumns);
  const requestRef = useRef(request);
  layoutColumnsRef.current = layoutColumns;
  requestRef.current = request;

  useEffect(() => {
    if (compact) return;
    let cancelled = false;
    const layout = loadMonitorLayout(request);
    void layout.result.then((stored) => {
      if (cancelled) return;
      syncMonitorLayout(stored);
      if (!monitorLayoutSaveActive()) {
        setLayoutColumns(applySavedPositionColumns(stored?.positionColumns ?? []));
        setView(stored?.positionGroupBy ?? 'total');
      }
      setLayoutReady(true);
    });
    return () => {
      cancelled = true;
      layout.cancel();
    };
  }, [compact, request]);

  /** Outside the Custom layout the grouping is the user's own: it is saved with their columns and comes back on the next visit. */
  const chooseView = (next: OmsPositionView) => {
    if (next === view) return;
    setView(next);
    if (!compact) void queueMonitorLayoutSave(requestRef.current, { positionGroupBy: next });
  };

  const saveColumns = (next: OmsLayoutColumn[]) => {
    const previous = layoutColumnsRef.current;
    layoutColumnsRef.current = next;
    setLayoutColumns(next);
    if (compact) {
      onColumnsChange?.(next);
      return;
    }
    void queueMonitorLayoutSave(requestRef.current, { positionColumns: next }).then((saved) => {
      if (!saved && layoutColumnsRef.current === next) {
        layoutColumnsRef.current = previous;
        setLayoutColumns(previous);
      }
    });
  };

  const onColumnsReorder = (fields: string[]) => {
    const current = layoutColumnsRef.current;
    const next = applyFieldOrder(current, fields);
    if (sameColumns(next, current)) return;
    saveColumns(next);
  };

  const onColumnWidthChange = ({ colDef, width }: { colDef: { field: string }; width: number }) => {
    const current = layoutColumnsRef.current;
    const next = current.map((column) => (column.id === colDef.field ? { ...column, width } : column));
    if (sameColumns(next, current)) return;
    saveColumns(next);
  };

  const viewLabel = VIEWS.find((item) => item.id === view)?.label ?? view;

  const rows = useMemo(() => (view === 'total' ? (positions ?? []).map(toRow) : []), [positions, view]);
  const settled = (positions !== null || failed) && layoutReady;
  const emptyMessage = view === 'total' ? (failed ? 'Positions unavailable.' : 'No positions for this trade day.') : GROUP_WIP;

  const exportCsv = () => {
    const header = layoutColumns.map((saved) => POSITION_COLUMN_DETAILS[saved.id as OmsPositionColumnId]?.label ?? saved.id);
    const lines = rows.map((row) =>
      layoutColumns.map((saved) => {
        const value = row[saved.id as keyof PositionRow];
        return value == null ? '' : String(value);
      }),
    );
    downloadCsv(csvFileName('oms-positions', date), toCsv(header, lines));
    track(GA.exportedOmsGridCsv, { layout: 'positions', format: 'csv' });
  };

  const columns = useMemo<Array<HFGridColDef<PositionRow>>>(
    () =>
      layoutColumns.map((saved) => {
        const details = POSITION_COLUMN_DETAILS[saved.id as OmsPositionColumnId];
        return column(saved.id as keyof PositionRow, details?.label ?? saved.id, saved.width ?? details?.width ?? 100, details?.tone);
      }),
    [layoutColumns],
  );

  return (
    <HFCard component="section" aria-label="Positions" aria-busy={!settled} sx={monitorPaneSx}>
      {compact ? (
        <PanelHeader title="Positions" summary={`Group by: ${viewLabel}`} open={chromeOpen} onToggle={() => setChromeOpen((open) => !open)} />
      ) : null}
      {!compact || chromeOpen ? (
        <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
          {compact ? null : (
            <HFBox sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
              <HFBox component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>
                Positions
              </HFBox>
              <HFTooltip title={NO_MARKET_VALUE}>
                <HFBox
                  component="button"
                  type="button"
                  aria-label={NO_MARKET_VALUE}
                  sx={{ display: 'inline-flex', p: 0, border: 0, bgcolor: 'transparent', color: 'text.secondary', cursor: 'help', lineHeight: 0 }}
                >
                  <InfoIcon sx={{ fontSize: 16 }} />
                </HFBox>
              </HFTooltip>
            </HFBox>
          )}
          {settled ? (
            <HFBox sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <HFBox component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
                GROUP BY
              </HFBox>
              {VIEWS.map((item) => (
                <HFButton
                  key={item.id}
                  size="small"
                  variant={view === item.id ? 'contained' : 'outlined'}
                  aria-pressed={view === item.id}
                  onClick={() => chooseView(item.id)}
                  sx={COMPACT_BUTTON_SX}
                >
                  {item.label}
                </HFButton>
              ))}
              <HFBox aria-hidden sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'divider' }} />
              <HFButton size="small" variant="outlined" sx={COMPACT_BUTTON_SX} onClick={exportCsv}>
                Export
              </HFButton>
            </HFBox>
          ) : (
            <HFBox data-testid="positions-group-skeleton" aria-hidden sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HFSkeleton variant="rounded" width={72} height={12} />
              <HFSkeleton variant="rounded" width={88} height={28} />
              <HFSkeleton variant="rounded" width={148} height={28} />
              <HFSkeleton variant="rounded" width={168} height={28} />
            </HFBox>
          )}
        </HFBox>
      ) : null}
      <HFBox sx={{ flex: 1, minHeight: 0 }}>
        {settled ? (
          <HFDataTable<PositionRow>
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            columnSearch={false}
            onColumnsReorder={onColumnsReorder}
            onColumnWidthChange={onColumnWidthChange}
            disableColumnMenu
            hideFooter
            fill
            autoHeight={false}
            autosizeOnMount={false}
            disableColumnVirtualization
            slots={{ noRowsOverlay: () => <EmptyPositions message={emptyMessage} /> }}
          />
        ) : (
          <TableSkeleton testId="positions-skeleton" />
        )}
      </HFBox>
    </HFCard>
  );
}
